import { Loader2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useDepartmentDistribution,
  useRoleDistribution,
  useTeamComparison,
} from '@/hooks'

import { DepartmentPieChart } from './department-pie-chart'
import { RoleBarChart } from './role-bar-chart'
import { TeamComparisonChart } from './team-comparison-chart'

interface StatisticsOverviewProps {
  onDepartmentClick?: (departmentId: string) => void
  onTeamClick?: (teamId: string) => void
}

export function StatisticsOverview({
  onDepartmentClick,
  onTeamClick,
}: StatisticsOverviewProps) {
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')

  const dateRange = {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  }

  const {
    data: departmentData,
    isLoading: isDepartmentLoading,
    error: departmentError,
  } = useDepartmentDistribution(dateRange)

  const {
    data: roleData,
    isLoading: isRoleLoading,
    error: roleError,
  } = useRoleDistribution(dateRange)

  const {
    data: teamData,
    isLoading: isTeamLoading,
    error: teamError,
  } = useTeamComparison(dateRange)

  const isLoading = isDepartmentLoading || isRoleLoading || isTeamLoading
  const hasError = departmentError || roleError || teamError

  const handleClearDateRange = () => {
    setStartDate('')
    setEndDate('')
  }

  return (
    <div className="space-y-6">
      {/* 日期范围选择器 */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">入职日期范围:</span>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-40"
            placeholder="开始日期"
          />
          <span className="text-muted-foreground">至</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-40"
            placeholder="结束日期"
          />
        </div>
        {(startDate || endDate) && (
          <Button variant="outline" size="sm" onClick={handleClearDateRange}>
            清除筛选
          </Button>
        )}
      </div>

      {/* 加载状态 */}
      {isLoading && (
        <div className="flex h-[200px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* 错误状态 */}
      {hasError && !isLoading && (
        <div className="flex h-[200px] items-center justify-center text-destructive">
          加载统计数据失败，请稍后重试
        </div>
      )}

      {/* 图表网格 */}
      {!isLoading && !hasError && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* 部门分布饼图 */}
          <div className="rounded-lg border bg-card p-4">
            <h3 className="mb-4 font-medium">部门分布</h3>
            <DepartmentPieChart
              data={departmentData ?? []}
              onSegmentClick={onDepartmentClick}
            />
          </div>

          {/* 角色分布柱状图 */}
          <div className="rounded-lg border bg-card p-4">
            <h3 className="mb-4 font-medium">角色分布</h3>
            <RoleBarChart data={roleData ?? []} />
          </div>

          {/* 团队对比柱状图 */}
          <div className="rounded-lg border bg-card p-4">
            <h3 className="mb-4 font-medium">团队对比</h3>
            <TeamComparisonChart
              data={teamData ?? []}
              onBarClick={onTeamClick}
            />
          </div>
        </div>
      )}
    </div>
  )
}
